import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker as XDatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { esES } from '@mui/x-date-pickers/locales';
import { isValid, parse, parseISO } from 'date-fns';
import { es } from 'date-fns/locale/es';

const parseLocalDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const d = parse(value, 'yyyy-MM-dd', new Date());
      return isValid(d) ? d : null;
    }
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }
  return null;
};

/** Returns the numeric timestamp for a valid Date, or null otherwise. */
const safeTime = (d) => (d instanceof Date && isValid(d) ? d.getTime() : null);

export default function DatePicker({
  id,
  value = null,
  label = 'Date',
  name,
  onChange,
  minDate = '1990-01-01',
  maxDate = '2100-01-01',
  error = false,
  helperText = '',
  disabled = false,
  fullWidth = false,
  size = 'medium',
}) {
  const [selectedDate, setSelectedDate] = useState(value);

  useEffect(() => {
    if (safeTime(value) !== safeTime(selectedDate)) {
      setSelectedDate(value ?? null);
    }
  }, [value]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date === null || isValid(date)) {
      onChange(name, date);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es} localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}>
      <XDatePicker
        enableAccessibleFieldDOMStructure={false}
        format="dd/MM/yyyy"
        label={label}
        minDate={parseLocalDate(minDate)}
        maxDate={parseLocalDate(maxDate)}
        value={selectedDate}
        disabled={disabled}
        onChange={handleDateChange}
        slotProps={{
          textField: {
            id,
            error: error,
            helperText: helperText,
            fullWidth: fullWidth,
            size,
            InputProps: {
              style: {
                backgroundColor: 'white',
                borderRadius: '12px',
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
}

DatePicker.propTypes = {
  id: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
  name: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'medium']),
};
